/**
 * Workout API Service
 * Handles all workout-related API operations
 */

import { apiClient } from '../apiClient';
import { WorkoutSession, ExerciseLog } from '@/types/workout';

// Active workout session type for real-time workout tracking
export interface ActiveWorkoutSession {
  sessionId: string;
  userId: string;
  week: number;
  day: string;
  session: string;
  exercises: any[];
  exerciseLogs: ExerciseLog[];
  currentExerciseIndex: number;
  status: 'in-progress' | 'paused' | 'completed';
  startTime: Date;
  lastActiveTime: Date;
  sessionNotes?: string;
}

// Request/Response types
export interface CreateSessionRequest {
  week: number;
  day: string;
  session: string;
  exercises: any[];
  date: string;
  notes?: string;
}

export interface UpdateSessionRequest {
  exerciseLogs?: ExerciseLog[];
  notes?: string;
  status?: 'planned' | 'in-progress' | 'completed' | 'skipped';
  completedAt?: string;
}

export interface GetSessionsParams {
  week?: number;
  day?: string;
  status?: string;
  limit?: number;
  offset?: number;
}

/**
 * Workout API Service Class
 */
class WorkoutApiService {
  /**
   * Create a new workout session
   */
  async createSession(data: CreateSessionRequest): Promise<{ id: string }> {
    return apiClient.post('/workouts', data);
  }

  /**
   * Get a specific workout session
   */
  async getSession(sessionId: string): Promise<WorkoutSession> {
    return apiClient.get(`/workouts/${sessionId}`);
  }

  /**
   * Update a workout session
   */
  async updateSession(sessionId: string, data: UpdateSessionRequest): Promise<void> {
    return apiClient.patch(`/workouts/${sessionId}`, data);
  }

  /**
   * Delete a workout session
   */
  async deleteSession(sessionId: string): Promise<void> {
    return apiClient.delete(`/workouts/${sessionId}`);
  }

  /**
   * Get user's workout sessions
   */
  async getUserSessions(params?: GetSessionsParams): Promise<WorkoutSession[]> {
    return apiClient.get('/workouts', params);
  }

  /**
   * Get workout sessions for a specific week
   */
  async getWeekSessions(week: number): Promise<WorkoutSession[]> {
    return apiClient.get('/workouts', { week });
  }

  // Active Session Management
  /**
   * Create a new active workout session
   */
  async createActiveSession(
    week: number,
    day: string,
    session: string,
    exercises: any[]
  ): Promise<string> {
    const response = await apiClient.post<{ sessionId: string }>('/workouts/active', {
      week,
      day,
      session,
      exercises,
    });
    return response.sessionId;
  }

  /**
   * Get an active workout session
   */
  async getActiveSession(sessionId: string): Promise<ActiveWorkoutSession> {
    return apiClient.get(`/workouts/active/${sessionId}`);
  }

  /**
   * Update an active workout session
   */
  async updateActiveSession(
    sessionId: string,
    updates: Partial<ActiveWorkoutSession>
  ): Promise<void> {
    return apiClient.patch(`/workouts/active/${sessionId}`, updates);
  }

  /**
   * Complete an active workout session
   */
  async completeActiveSession(sessionId: string): Promise<void> {
    return apiClient.post(`/workouts/active/${sessionId}/complete`);
  }

  /**
   * Get user's current active session
   */
  async getUserActiveSession(): Promise<ActiveWorkoutSession | null> {
    try {
      return await apiClient.get('/workouts/active/current');
    } catch (error: any) {
      if (error.statusCode === 404) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Pause an active workout session
   */
  async pauseActiveSession(sessionId: string): Promise<void> {
    return apiClient.post(`/workouts/active/${sessionId}/pause`);
  }

  /**
   * Resume an active workout session
   */
  async resumeActiveSession(sessionId: string): Promise<void> {
    return apiClient.post(`/workouts/active/${sessionId}/resume`);
  }
}

export const workoutApi = new WorkoutApiService();
